import { useEffect, useState } from 'react';
import axios from '../../lib/axios';

const ICONS_BY_MIME = {
    'application/pdf': 'picture_as_pdf',
    'application/zip': 'folder_zip',
    'application/msword': 'description',
};

function iconFor(mime) {
    if (!mime) return 'draft';
    if (mime.startsWith('image/')) return 'image';
    if (mime.startsWith('video/')) return 'movie';
    if (mime.startsWith('audio/')) return 'audiotrack';
    return ICONS_BY_MIME[mime] || 'draft';
}

function formatSize(bytes) {
    if (!bytes) return '';
    const units = ['B', 'KB', 'MB', 'GB'];
    let i = 0;
    let n = bytes;
    while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
    return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

/**
 * La descarga requiere el Bearer token (ver lib/axios.js), así que un simple
 * <a href> al endpoint no sirve — se pide como blob autenticado y se dispara
 * la descarga/preview desde ahí.
 */
async function fetchBlobUrl(attachmentId) {
    const response = await axios.get(`/api/email-attachments/${attachmentId}/download`, { responseType: 'blob' });
    return URL.createObjectURL(response.data);
}

function ImageThumb({ attachment }) {
    const [src, setSrc] = useState(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let url;
        let cancelled = false;
        fetchBlobUrl(attachment.id)
            .then((blobUrl) => {
                // Si el componente ya se desmontó, el cleanup corrió antes de
                // que resolviera la descarga: ahí `url` seguía undefined y no se
                // revocaba nada, así que la imagen entera quedaba retenida en
                // memoria hasta recargar la página. Se revoca acá mismo.
                if (cancelled) {
                    URL.revokeObjectURL(blobUrl);
                    return;
                }
                url = blobUrl;
                setSrc(blobUrl);
            })
            .catch(() => { if (!cancelled) setFailed(true); });
        return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
    }, [attachment.id]);

    if (failed) return null;

    return (
        <a href={src || '#'} target="_blank" rel="noreferrer" onClick={(e) => !src && e.preventDefault()}
            className="block w-28 h-28 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-slate-800">
            {src ? (
                <img src={src} alt={attachment.filename} className="w-full h-full object-cover" />
            ) : (
                <div className="w-full h-full flex items-center justify-center">
                    <span className="material-symbols-outlined text-gray-300 animate-pulse">image</span>
                </div>
            )}
        </a>
    );
}

function FileChip({ attachment }) {
    const [downloading, setDownloading] = useState(false);

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const blobUrl = await fetchBlobUrl(attachment.id);
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = attachment.filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(blobUrl);
        } catch {
            alert('No se pudo descargar el adjunto.');
        } finally {
            setDownloading(false);
        }
    };

    return (
        <button onClick={handleDownload} disabled={downloading}
            className="text-xs px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg flex items-center gap-1.5 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50">
            <span className="material-symbols-outlined text-sm">{downloading ? 'progress_activity' : iconFor(attachment.mime_type)}</span>
            {attachment.filename}
            {attachment.size ? <span className="text-gray-400">({formatSize(attachment.size)})</span> : null}
        </button>
    );
}

export default function AttachmentList({ attachments }) {
    if (!attachments || attachments.length === 0) return null;

    const images = attachments.filter((a) => a.mime_type?.startsWith('image/'));
    const others = attachments.filter((a) => !a.mime_type?.startsWith('image/'));

    return (
        <div className="mt-4 space-y-3">
            {images.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {images.map((att) => <ImageThumb key={att.id} attachment={att} />)}
                </div>
            )}
            {others.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {others.map((att) => <FileChip key={att.id} attachment={att} />)}
                </div>
            )}
        </div>
    );
}
